import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import FallbackImg from "../../../components/FallbackImg";
import Policy from "../../../assets/aftersalesservice_icons/policy.svg";
import Download from "../../../assets/aftersalesservice_icons/warranty_download.svg";
import BackBlue from "../../../assets/aftersalesservice_icons/back_blue_icon.svg";
import { useLanguage } from "../LanguageContext";
import { buildAfterSalesQuery } from "../serialTypeUtils";
import ApiService from "../../../components/Api_services";

// Mix a hex color towards white so it stays visible on top of that same
// color used solid as the card background (alpha-blending a color with
// itself, e.g. "#bb4d0080", just reproduces the same color with no contrast)
// const lightenColor = (hex, amount = 0.5) => {
//   if (!hex) return null;

//   const clean = hex.replace("#", "");
//   const normalized =
//     clean.length === 3
//       ? clean
//           .split("")
//           .map((c) => c + c)
//           .join("")
//       : clean;
//   const value = parseInt(normalized, 16);
//   if (Number.isNaN(value) || normalized.length !== 6) return hex;

//   const r = (value >> 16) & 255;
//   const g = (value >> 8) & 255;
//   const b = value & 255;
//   const lighten = (channel) => Math.round(channel + (255 - channel) * amount);

//   return `rgb(${lighten(r)}, ${lighten(g)}, ${lighten(b)})`;
// };

// Render a lucide-react icon by name (e.g. "Info", "CircleCheck"), as returned by the API
const SectionIcon = ({ name, color, size = 20 }) => {
  const IconComp = name && LucideIcons[name];
  return IconComp ? (
    <IconComp
      color={color || undefined}
      size={size}
      style={{ flexShrink: 0 }}
    />
  ) : null;
};

const WarrantyPolicy = () => {
  const {
    trans,
    lang,
    countryCode: country,
    itemId1,
    itemId2,
    warrantyLoaded,
  } = useLanguage();
  const [searchParams] = useSearchParams();
  const sn = atob(searchParams.get("sn") || "");
  const getSerialNo = () => sn;
  const item_id = [itemId1, itemId2].filter(Boolean).map(Number);
  const [warrantyPolicy, setWarrantyPolicy] = useState(null);
  const [warrantyPolicyPdf, setWarrantyPolicyPdf] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const sections = useMemo(() => {
    const item = (warrantyPolicy || [])[0];
    return (item?.sections || [])
      .slice()
      .sort((a, b) => (Number(a.seq_no) || 0) - (Number(b.seq_no) || 0));
  }, [warrantyPolicy]);

  const openPdf = () => {
    if (warrantyPolicyPdf) window.open(warrantyPolicyPdf, "_blank");
  };

  const goBack = (e) => {
    e.preventDefault();
    window.history.back();
  };

  useEffect(() => {
    const fetchWarrantyPolicy = async () => {
      const apiLang = lang === "ch" ? "cn" : lang === "my" ? "ms" : lang;
      setLoaded(false);

      try {
        const response = await ApiService.getWarrantyPolicy(
          item_id,
          apiLang,
          country,
        );
        if (response && response.status === 200) {
          setWarrantyPolicy(response.data);
          setWarrantyPolicyPdf(response.data[0]?.pdf_file);
        } else {
          setWarrantyPolicy(null);
          setWarrantyPolicyPdf(null);
          console.error("Error fetching warranty policy:", response.message);
        }
      } catch (error) {
        setWarrantyPolicy(null);
        setWarrantyPolicyPdf(null);
        console.error("Error fetching warranty policy:", error);
      } finally {
        setLoaded(true);
      }
    };

    // wait for LanguageContext to resolve the country for this serial
    if (!warrantyLoaded) return;
    fetchWarrantyPolicy();
  }, [itemId1, itemId2, country, lang, warrantyLoaded]);

  // shown once the fetch has finished (success or error) with no sections
  const emptySections = loaded && sections.length === 0 && (
    <div className="as-user-manual-item" style={{ backgroundColor: "#ffffff" }}>
      <div className="as-no-results" style={{ display: "block" }}>
        <div>{trans.no_warranty_policy}</div>
      </div>
    </div>
  );

  return (
    <AfterSalesLayout>
      <div className="as-warranty-policy-content" id="warranty_policy_modal">
        <div className="as-warranty-policy-container">
          <div style={{ padding: "0 31.97px" }}>
            <div className="as-warranty-policy-card">
              <a
                className="as-back-btn"
                id="warranty_policy_back"
                href="#"
                onClick={goBack}
              >
                <FallbackImg
                  src={BackBlue}
                  className="as-back_blue_icon"
                  alt="back"
                />
                {trans.back}
              </a>

              <div className="as-card-header">
                <FallbackImg
                  src={Policy}
                  className="as-warranty_icon"
                  alt="policy"
                />

                <div className="as-text">
                  <h2>{trans.warr_policy}</h2>
                  <p className="as-warranty-policy-subtitle">
                    {trans.warr_policy_sub}
                  </p>
                </div>
              </div>

              <button
                className="as-download-btn"
                type="button"
                onClick={openPdf}
              >
                <FallbackImg
                  src={Download}
                  className="as-download_icon"
                  alt="download"
                />
                {trans.download_policy}
              </button>
            </div>

            {sections.map((section) => (
              <div
                className="as-user-manual-item"
                key={section.id}
                style={{
                  backgroundColor: section.background_color
                    ? section.background_color
                    : "#ffffff",
                  color: section.text_color ? section.text_color : "#000000",
                }}
              >
                <div className="as-user-manual-title">
                  <div
                    className="as-user-manual-title-icon"
                    // style={{
                    //   backgroundColor: section.background_color
                    //     ? lightenColor(section.background_color)
                    //     : "#f0f0f0",
                    // }}
                  >
                    <SectionIcon
                      name={section.icon}
                      color={section.text_color}
                    />
                  </div>
                  <div>{section.description}</div>
                </div>
                {(section.children || []).map((child) => (
                  <div
                    className="as-user-manual-box"
                    key={child.id}
                    style={{
                      backgroundColor: child.background_color
                        ? child.background_color
                        : "transparent",
                      color: child.text_color ? child.text_color : "#000000",
                    }}
                  >
                    <SectionIcon name={child.icon} color={child.text_color} />
                    <div>{child.description}</div>
                  </div>
                ))}
              </div>
            ))}
            {emptySections}

            <div className="as-contact-card">
              <p>
                {trans.ques_warr}
                <br />
                {trans.our_sup_1}
              </p>

              <Link
                to={`/aftersalesservice/ticketsubmission${buildAfterSalesQuery(
                  searchParams,
                  {
                    type: "Technical_Consultation_Inquiry",
                  },
                )}`}
              >
                <button className="as-contact-btn" type="button">
                  {trans.contact_sup}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AfterSalesLayout>
  );
};

export default WarrantyPolicy;
